import { BehaviorSubject, Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import {
  Airport,
  Country,
  DisplayText,
  EquipmentClass,
  ListManagerTargetingData,
  ListsMap,
  RuleData,
  Subfleet,
  Tail,
  Terminal,
} from './../uas/editing/targeting/targeting-data.interface';

@Injectable({ providedIn: 'root' })
export class ListManagerService {
  // invariant: `init()` is called before any of the below are read.
  listsMap!: ListsMap;
  targetingData!: ListManagerTargetingData;

  ruleData!: RuleData;
  dataMaps!: DataMaps;

  amenity_option_list_types = ['layout', 'seat', 'entertainment', 'power', 'food', 'beverage', 'wifi'];

  setActiveRuleOption?: (optionId: number) => void;

  private dataSource = new BehaviorSubject<ActiveListOption | null>(null);
  constructor() { }

  activeOptionId: Observable<ActiveListOption | null> = this.dataSource.asObservable();
  
  init(listsMap: ListsMap, targetingData: ListManagerTargetingData, ruleData: RuleData, optionId?: number) {
    this.listsMap = listsMap;
    this.targetingData = targetingData;
    this.ruleData = ruleData;
    this.dataMaps = {
      subfleets: this.mapBy(this.targetingData.subfleets, 'id'),
      terminals: this.mapBy(this.targetingData.terminals, 'code'),
      countries: this.mapBy(this.targetingData.countries, 'code'),
      airports: this.mapBy(this.targetingData.airports, 'code'),
      // tails: this.mapBy(this.targetingData.tails, 'id'),
      equipmentClass:  this.mapBy(this.targetingData.equipmentClass, 'name')
    };
    
  }

  // `list` is polymorphic by listType: the selected items (with id/code/name) for
  // most types, or a Route[] for origin_and_destination — kept `any[]`.
  addList(id: number, listType: string, list: any[], name: string, listSubType?: string) {
    const newOption: Record<string, unknown> = {
      id: id,
      name: name,
    };
    if (listType === 'subfleet') {
      newOption.subfleet_ids = list.map(item => item.id);
    } else if (listType === 'terminal') {
      newOption.terminal_codes = list.map(item => item.code);
    } else if (listType === 'country') {
      newOption.country_codes = list.map(item => item.code);
    } else if (listType === 'airport') {
      newOption.airport_codes = list.map(item => item.code);
    } else if (listType === 'origin_and_destination') {
      newOption.routes = list;
      newOption.type = listSubType;
    } else if (this.amenity_option_list_types.includes(listType)) {
      newOption.display_texts = list.map(item => ({
        display_text_name: item.name,
        display_text_key: item.id
      }));
    }
    else if (listType === 'equipment_class') {
      newOption.equipment_class_values = list.map(item => {
       return item.name
      });
    }

    this.ruleData.options[listType + '_list'].unshift(newOption);
    //Note: listType and newList need to be sent to prevent an issue of list update not showing on the option dropdown.
    this.dataSource.next({ id: id, ruleData: this.ruleData, listType: listType, newList: this.ruleData.options[listType + '_list'] });
    
  }

  // The `list` param in this and the other *Build/populate helpers is the mutable
  // rule-option buffer being enriched with `rule_content`; its shape varies by
  // option type, so it stays `any`.
  private simpleListBuild(list: any, optionType: string, field: string, map: Record<string, unknown>) {
    list.rule_content = [];
    //For example: values of all terminal_codes are added here.
    list[optionType + '_' + field + 's'].forEach((id: number) => {
      const item = map[id];
      if (item) {
        list.rule_content.push(item);
      }
    });
  }

  //Note: Function to check if it's an amenity type option.
  private isAmenityOptionType(option_type_id: string) {
    const amenity_option_type_ids = ['layout', 'seat', 'entertainment', 'power', 'food', 'beverage', 'wifi']
    return amenity_option_type_ids.includes(option_type_id);
  }
 
  private isAmenityList(option_type_id: string) {
    const amenityLists = ['layout_list', 'seat_list', 'entertainment_list', 'power_list', 'food_list', 'beverage_list', 'wifi_list'];
    return amenityLists.includes(option_type_id);
  }

  private amenityListBuild(list: any) {
    list.rule_content = [];
    list.display_texts.forEach((display: DisplayText) => {
      const item = {
        code: display.display_text_key,
        name: display.display_text_name
      }
      list.rule_content.push(item)
    });
  }

  populateListContent(list: any, option_type_id: string) {
    if (list.rule_content) {
      return;
    }

    if (option_type_id === 'subfleet_list') {
      this.simpleListBuild(list, 'subfleet', 'id', this.dataMaps.subfleets);
    } else if (option_type_id === 'terminal_list') {
      this.simpleListBuild(list, 'terminal', 'code', this.dataMaps.terminals);
    } else if (option_type_id === 'country_list') {
      this.simpleListBuild(list, 'country', 'code', this.dataMaps.countries);
    } else if (option_type_id === 'airport_list' || option_type_id === 'stopover_connection_list') {
      this.simpleListBuild(list, 'airport', 'code', this.dataMaps.airports);
    }

    else if (option_type_id === 'equipment_class_list') {
    
      list.rule_content = [];
      //Need to push with a "name" property to support inner list values' display on expandable-list
      list.equipment_class_values?.forEach((eqClassVal: string) => {
        list.rule_content.push({name: eqClassVal})
      });
    }
    //Contains subfleets
    else if (option_type_id === 'equipment_class' || this.isAmenityOptionType(option_type_id)) {
      list.rule_content = [];
      list.subfleets?.forEach((sf: { id: number; name: string }) => {list.rule_content.push(sf)});
    }
     else if (option_type_id === 'origin_and_destination_list') {
      list.rule_content = [];
      let dataset: any;
      if (list.type === 'airport') {
        dataset = this.dataMaps.airports;
      } else {
        dataset = this.dataMaps.countries;
      }
      list.routes.forEach((route: any) => {
        const from = dataset[route.from];
        const to = dataset[route.to];
        let both_ways = '';
        if (route.both_ways) {
          both_ways =
            '<svg class="icon icon-arrow-right-arrow-left od-list__rule-icon"><use xlink:href="#icon-arrow-right-arrow-left"></use></svg>';
        }
        list.rule_content.push({
          name: `
            <div class="od-both-ways">
              <div class="od-rule-content-airport">${from.name}</div>
              <div class="od-rule-content-airport"> to ${to.name}</div>
              ${both_ways}
            </div>
          `,
        });
      });
    } else if (this.isAmenityList(option_type_id)) {
      this.amenityListBuild(list);
    }
  }

  mapBy<T>(objects: T[] | undefined, field: keyof T): Record<string, T> {
    const map: Record<string, T> = {};
    objects?.forEach((item) => {
      map[String(item[field])] = item;
    });
    return map;
  }
}

export interface DataMaps {
  subfleets: Record<string, Subfleet>;
  terminals: Record<string, Terminal>;
  countries: Record<string, Country>;
  airports: Record<string, Airport>;
  tails?: Record<string, Tail>;
  equipmentClass?: Record<string, EquipmentClass>;
}

// Emitted on activeOptionId when addList creates a new option. The stream seeds
// `null` (no option selected yet), so these fields are all required — a real event
// always carries every one. `newList` holds the rule-option objects for a list type,
// heterogeneous by type, hence Record<string, unknown>[] rather than one interface.
interface ActiveListOption {
  id: number;
  ruleData: RuleData;
  listType: string;
  newList: Record<string, unknown>[];
}
